import {
  CategoryCode,
  Coverage,
  Dropdown,
  Duration,
  extractCoverageFromCoverage,
  getPersonInsuranceTypeFromDuration,
  JobRiskClass,
  PersonInsuranceType,
  Protection,
  RiderType,
  Sex,
} from '@emma-labs/shared/typescript';
import {BehaviorSubject, combineLatest, debounceTime, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';

export interface SelectorCardInput {
  coverageOptions?: Dropdown<number>[];
  durationOptions?: Dropdown<Duration | number>[];
  price?: number;
  renewalPrices?: {duration: Duration; price: number; renewalPrices: number[]};
  prefilledCoverage?: {coverageAmount: number; duration?: Duration | number};
}

export function createProtectionBuilderObservablesAndSubjects() {
  const builtProtectionSubject = new BehaviorSubject<Protection | null>(null);

  const policyFee$ = builtProtectionSubject.pipe(
    map((protection) =>
      protection?.personInsurances.reduce((acc, pi) => acc + pi.priceBreakdown.policyFee, 0),
    ),
  );

  const totalPrice$ = builtProtectionSubject.pipe(
    map((protection) => protection?.personInsurances.reduce((acc, pi) => acc + pi.price, 0)),
  );

  const coverageSubject = new BehaviorSubject<Coverage | null | undefined>(undefined);
  const totalCoverage$ = coverageSubject.pipe(
    map((coverage) => Object.values(coverage?.lifeInsurances ?? {}).reduce((acc, val) => acc + val, 0)),
  );
  const ageSubject = new BehaviorSubject<number | null>(null);
  const sexSubject = new BehaviorSubject<Sex | null>(null);
  const smokerSubject = new BehaviorSubject<boolean | null>(null);
  const categoryCodeSubject = new BehaviorSubject<CategoryCode | null>(null);
  const jobRiskClassSubject = new BehaviorSubject<JobRiskClass | null>(null);
  const surchargeRateSubject = new BehaviorSubject<number | null>(null);

  const termLifeCoverageAmountSubject = new BehaviorSubject<number>(0);
  const termLifeCoverageAmountOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const termLifeDurationOptionsSubject = new BehaviorSubject<Dropdown<Duration>[]>([]);
  const termLifeInputs$: Observable<SelectorCardInput> = combineLatest([
    termLifeCoverageAmountOptionsSubject,
    termLifeDurationOptionsSubject.pipe(debounceTime(250)),
    builtProtectionSubject.pipe(
      map((protection) => (protection ? getPersonInsurancePrice(protection, 'TERM') : undefined)),
      startWith(undefined),
    ),
    coverageSubject.pipe(
      map((coverage) => {
        if (!coverage) {
          return undefined;
        }
        return extractCoverageFromCoverage(coverage, 'TERM');
      }),
    ),
  ]).pipe(
    map(([coverageOptions, durationOptions, price, prefilledCoverage]) => ({
      coverageOptions,
      durationOptions,
      price,
      prefilledCoverage,
    })),
  );

  const wholeLifeCoverageAmountSubject = new BehaviorSubject<number>(0);
  const wholeLifeCoverageAmountOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const wholeLifeDurationOptionsSubject = new BehaviorSubject<Dropdown<Duration>[]>([]);
  const wholeLifeInputs$: Observable<SelectorCardInput> = combineLatest([
    wholeLifeCoverageAmountOptionsSubject,
    wholeLifeDurationOptionsSubject.pipe(debounceTime(250)),
    builtProtectionSubject.pipe(
      map((protection) => (protection ? getPersonInsurancePrice(protection, 'WHOLE') : undefined)),
      startWith(undefined),
    ),
    coverageSubject.pipe(
      map((coverage) => {
        if (!coverage) {
          return undefined;
        }
        return extractCoverageFromCoverage(coverage, 'WHOLE');
      }),
    ),
  ]).pipe(
    map(([coverageOptions, durationOptions, price, prefilledCoverage]) => ({
      coverageOptions,
      durationOptions,
      price,
      prefilledCoverage,
    })),
  );

  const childRiderCoverageAmountOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const childRiderInputs$: Observable<SelectorCardInput> = combineLatest([
    childRiderCoverageAmountOptionsSubject,
    builtProtectionSubject.pipe(
      map(
        (protection) =>
          protection?.personInsurances
            .find((pi) => pi.riders?.length)
            ?.riders?.find((r) => r.type === RiderType.CHILD)?.price,
      ),
      startWith(undefined),
    ),
    coverageSubject.pipe(
      map((coverage) => {
        if (!coverage) {
          return undefined;
        }
        return extractCoverageFromCoverage(coverage, RiderType.CHILD);
      }),
    ),
  ]).pipe(
    map(([coverageOptions, price, prefilledCoverage]) => ({
      coverageOptions,
      price,
      prefilledCoverage,
    })),
  );

  const disabilityRiderCoverageAmountOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const disabilityRiderDurationOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const disabilityRiderInputs$: Observable<SelectorCardInput> = combineLatest([
    disabilityRiderCoverageAmountOptionsSubject,
    disabilityRiderDurationOptionsSubject,
    builtProtectionSubject.pipe(
      map(
        (protection) =>
          protection?.personInsurances
            .find((pi) => pi.riders?.length)
            ?.riders?.find((r) => r.type === RiderType.DISABILITY)?.price,
      ),
      startWith(undefined),
    ),
    coverageSubject.pipe(
      map((coverage) => {
        if (!coverage) {
          return undefined;
        }
        return extractCoverageFromCoverage(coverage, RiderType.DISABILITY);
      }),
    ),
  ]).pipe(
    map(([coverageOptions, durationOptions, price, prefilledCoverage]) => ({
      coverageOptions,
      durationOptions,
      price,
      prefilledCoverage,
    })),
  );

  const cancerRiderCoverageAmountOptionsSubject = new BehaviorSubject<Dropdown<number>[]>([]);
  const cancerRiderInputs$: Observable<SelectorCardInput> = combineLatest([
    cancerRiderCoverageAmountOptionsSubject,
    builtProtectionSubject.pipe(
      map(
        (protection) =>
          protection?.personInsurances
            .find((pi) => pi.riders?.length)
            ?.riders?.find((r) => r.type === RiderType.CANCER)?.price,
      ),
      startWith(undefined),
    ),
    coverageSubject.pipe(
      map((coverage) => {
        if (!coverage) {
          return undefined;
        }
        return extractCoverageFromCoverage(coverage, RiderType.CANCER);
      }),
    ),
  ]).pipe(
    map(([coverageOptions, price, prefilledCoverage]) => ({
      coverageOptions,
      price,
      prefilledCoverage,
    })),
  );

  return {
    builtProtectionSubject,
    policyFee$,
    totalPrice$,
    coverageSubject,
    totalCoverage$,
    ageSubject,
    sexSubject,
    smokerSubject,
    categoryCodeSubject,
    jobRiskClassSubject,
    surchargeRateSubject,
    termLifeCoverageAmountSubject,
    termLifeCoverageAmountOptionsSubject,
    termLifeDurationOptionsSubject,
    termLifeInputs$,
    wholeLifeCoverageAmountSubject,
    wholeLifeCoverageAmountOptionsSubject,
    wholeLifeDurationOptionsSubject,
    wholeLifeInputs$,
    childRiderCoverageAmountOptionsSubject,
    childRiderInputs$,
    disabilityRiderCoverageAmountOptionsSubject,
    disabilityRiderDurationOptionsSubject,
    disabilityRiderInputs$,
    cancerRiderCoverageAmountOptionsSubject,
    cancerRiderInputs$,
  };
}

function getPersonInsurancePrice(protection: Protection, personInsuranceType: PersonInsuranceType) {
  const personInsurance = protection.personInsurances.find(
    (pi) => getPersonInsuranceTypeFromDuration(pi.duration) === personInsuranceType,
  );

  if (!personInsurance) {
    return undefined;
  }

  return personInsurance.priceBreakdown.basePremium + personInsurance.priceBreakdown.surchargeAmount;
}
